import { isDefined } from '@sgme/fp';
import type { ScheduledRunOccurrence, ScheduledRunOccurrenceStatus } from 'apiConnector/scheduledRun/model';
import { DateRangePicker, type DatesRange } from 'components/shared/DatePicker/DatesRangePicker';
import { isIsoDate, withoutTimePart } from 'components/shared/DatePicker/utils';
import { ErrorAlert } from 'components/shared/ScheduledRun/ErrorAlert';
import { ScheduledRunModalFooter } from 'components/shared/ScheduledRun/Modal/ScheduledRunModalFooter';
import { ScheduledRunModalHeader } from 'components/shared/ScheduledRun/Modal/ScheduledRunModalHeader';
import { useCopyTextToClipboard } from 'hooks/useCopyToClipboard';
import { useThunkDispatch } from 'hooks/useThunkDispatch';
import type { IntlKey } from 'locales';
import { useEffect, useId, useState } from 'react';
import { Modal, ModalBody, Spinner } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { EDITABLE_SCHEDULED_RUN_BLOTTER_ID, getEditableScheduledRun } from 'state/blotters/scheduledQuotes';
import { scheduledRunActions } from 'state/scheduledRuns/actions';
import { openScheduledRunOccurrencesThunk, requestScheduledRunOccurrenceDetailThunk } from 'state/scheduledRuns/thunks';
import type { AppState } from 'state/state.model';
import type { ColorLevel } from 'types/sgBootstrap';
import { displayDateFormatOptions } from 'utils/date';
import { asError, asSuccess } from 'utils/status';

const MODAL_ID = 'scheduled-run-occurrence-reload-modal';
const DATE_RANGE_ID = 'scheduled-run-occurrence-date-range';
const OCCURRENCE_STATUS_COLORS: Record<ScheduledRunOccurrenceStatus, ColorLevel> = {
  Error: 'danger',
  PartialSuccess: 'warning',
  Success: 'success',
};

export function OccurrenceReloadModal() {
  const dispatch = useThunkDispatch();
  const { formatMessage } = useIntl();
  const copyToClipboard = useCopyTextToClipboard();
  const [dateRange, setDateRange] = useState<DatesRange>({});
  const scheduledRun = useSelector((state: AppState) => getEditableScheduledRun(state));
  const occurrences = asSuccess(scheduledRun.occurrences)?.content;
  const occurrenceError = asError(scheduledRun.occurrences)?.error;
  const occurrenceDetailError = asError(scheduledRun.occurrenceDetail)?.error;
  const reload = scheduledRun.occurrenceReload;
  const isReloading = reload.status === 'loading';
  const isLoading =
    scheduledRun.occurrences.status === 'loading' || scheduledRun.occurrenceDetail.status === 'loading' || isReloading;
  const skippedCount = reload.skippedCount + reload.skippedQuoteIds.length;
  const firstOccurrenceDate = getFirstOccurrenceDate(occurrences);
  const scheduleEndDate = getCalendarDate(scheduledRun.range?.endDate);
  const hasDateFilter = dateRange.startDate !== undefined || dateRange.endDate !== undefined;
  const filteredOccurrences = occurrences?.filter(occurrence => isOccurrenceInDateRange(occurrence, dateRange));
  const isSelectedOccurrenceVisible = filteredOccurrences?.some(
    occurrence => occurrence.occurrenceId === scheduledRun.selectedOccurrenceId,
  );

  useEffect(() => {
    if (scheduledRun.selectedOccurrenceId !== undefined && filteredOccurrences && !isSelectedOccurrenceVisible) {
      dispatch(scheduledRunActions.occurrenceSelected(EDITABLE_SCHEDULED_RUN_BLOTTER_ID, undefined));
    }
  }, [dispatch, filteredOccurrences, isSelectedOccurrenceVisible, scheduledRun.selectedOccurrenceId]);

  const close = () => {
    setDateRange({});
    dispatch(scheduledRunActions.occurrenceReloadReset(EDITABLE_SCHEDULED_RUN_BLOTTER_ID, reload.operationId));
  };
  const retry = () => dispatch(openScheduledRunOccurrencesThunk(EDITABLE_SCHEDULED_RUN_BLOTTER_ID));
  const retryDetail = () => dispatch(requestScheduledRunOccurrenceDetailThunk(EDITABLE_SCHEDULED_RUN_BLOTTER_ID));
  const copyOccurrenceQuoteIds = (occurrence: ScheduledRunOccurrence) => {
    copyToClipboard(getOccurrenceQuoteIds(occurrence).join('\n'));
  };
  const error = occurrenceError ?? occurrenceDetailError;

  return (
    <Modal show={scheduledRun.occurrenceModalOpen} onHide={close} id={MODAL_ID}>
      <ScheduledRunModalHeader
        title="scheduledRun.occurrenceReload.modal.title"
        loadingStatus={isLoading ? 'loading' : 'idle'}
        onClosed={close}
      />
      <ModalBody className="d-flex flex-column min-h-0 overflow-visible pb-2">
        <p className="text-secondary">
          <FormattedMessage id="scheduledRun.occurrenceReload.modal.description" />
        </p>
        <label className="form-label" htmlFor={`${DATE_RANGE_ID}-input`}>
          <FormattedMessage id="scheduledRun.occurrenceReload.dateRange" />
        </label>
        <DateRangePicker
          id={DATE_RANGE_ID}
          className="mb-3"
          closeWhenClickOutside
          disabled={isLoading}
          endDate={dateRange.endDate}
          endPlaceholder={formatMessage({ id: 'scheduledRun.occurrenceReload.endDate' })}
          hidePresets
          maxDate={scheduleEndDate}
          minDate={firstOccurrenceDate}
          onChange={setDateRange}
          startDate={dateRange.startDate}
          startPlaceholder={formatMessage({ id: 'scheduledRun.occurrenceReload.startDate' })}
        />
        {scheduledRun.occurrences.status === 'loading' && (
          <div className="d-flex align-items-center justify-content-center p-4" role="status">
            <Spinner className="me-2" size="sm" />
            <FormattedMessage id="loading" />
          </div>
        )}
        {scheduledRun.occurrenceDetail.status === 'loading' && (
          <div className="d-flex align-items-center justify-content-center p-4" role="status">
            <Spinner className="me-2" size="sm" />
            <FormattedMessage id="loading" />
          </div>
        )}
        {occurrences?.length === 0 && (
          <div className="alert alert-discreet-info" role="status">
            <FormattedMessage id="scheduledRun.occurrenceReload.noOccurrences" />
          </div>
        )}
        {hasDateFilter && occurrences && occurrences.length > 0 && filteredOccurrences?.length === 0 && (
          <div className="alert alert-discreet-info" role="status">
            <FormattedMessage id="scheduledRun.occurrenceReload.noMatchingOccurrences" />
          </div>
        )}
        {filteredOccurrences && filteredOccurrences.length > 0 && (
          <div className="list-group list-group-flush flex-grow-1 min-h-0 overflow-y-auto" role="radiogroup">
            {filteredOccurrences.map((occurrence, index) => (
              <OccurrenceRow
                key={`${occurrence.occurrenceId ?? 'occurrence'}-${index}`}
                occurrence={occurrence}
                disabled={isLoading || !occurrence.occurrenceId?.trim()}
                isSelected={occurrence.occurrenceId === scheduledRun.selectedOccurrenceId}
                onCopy={() => copyOccurrenceQuoteIds(occurrence)}
                onSelected={() =>
                  occurrence.occurrenceId?.trim() &&
                  dispatch(
                    scheduledRunActions.occurrenceSelected(EDITABLE_SCHEDULED_RUN_BLOTTER_ID, occurrence.occurrenceId),
                  )
                }
              />
            ))}
          </div>
        )}
        {error && (
          <>
            <ErrorAlert {...error} />
            <button
              className="btn btn-outline-secondary align-self-end"
              disabled={isLoading}
              onClick={occurrenceError ? retry : retryDetail}
            >
              <FormattedMessage id="retry" />
            </button>
          </>
        )}
        {isReloading && (
          <div className="alert alert-discreet-info mt-2" role="status">
            <FormattedMessage id="scheduledRun.occurrenceReload.reloading" />
          </div>
        )}
        {reload.status === 'success' && (
          <div className="alert alert-discreet-info mt-2" role="status">
            <FormattedMessage
              id="scheduledRun.occurrenceReload.result"
              values={{
                succeeded: reload.succeededQuoteIds.length,
                failed: reload.failedQuoteIds.length,
                skipped: skippedCount,
              }}
            />
          </div>
        )}
      </ModalBody>
      <ScheduledRunModalFooter
        error={error}
        hasHelpBtn={error !== undefined}
        modalId={MODAL_ID}
        cancelBtn={
          <button className="btn btn-outline-secondary btn-lg flex-grow-1 me-1" onClick={close}>
            <FormattedMessage id="cancel" />
          </button>
        }
        validateBtn={
          <button
            className="btn btn-primary btn-lg flex-grow-1 ms-1"
            disabled={
              !isSelectedOccurrenceVisible ||
              scheduledRun.occurrences.status !== 'success' ||
              isLoading ||
              reload.status !== 'idle'
            }
            onClick={() => dispatch(requestScheduledRunOccurrenceDetailThunk(EDITABLE_SCHEDULED_RUN_BLOTTER_ID))}
          >
            <FormattedMessage id="scheduledRun.occurrenceReload.confirm" />
          </button>
        }
      />
    </Modal>
  );
}

function getCalendarDate(value: string | undefined) {
  if (!value) {
    return undefined;
  }

  const calendarDate = withoutTimePart(value);
  return isIsoDate(calendarDate) ? calendarDate : undefined;
}

function getFirstOccurrenceDate(occurrences: readonly ScheduledRunOccurrence[] | undefined) {
  return occurrences?.reduce<string | undefined>((firstDate, occurrence) => {
    const occurrenceDate = getCalendarDate(occurrence.occurrenceDate);
    return occurrenceDate && (firstDate === undefined || occurrenceDate < firstDate) ? occurrenceDate : firstDate;
  }, undefined);
}

function isOccurrenceInDateRange(occurrence: ScheduledRunOccurrence, range: DatesRange) {
  if (range.startDate === undefined && range.endDate === undefined) {
    return true;
  }

  const occurrenceDate = getCalendarDate(occurrence.occurrenceDate);
  if (!occurrenceDate) {
    return false;
  }

  return (
    (range.startDate === undefined || occurrenceDate >= range.startDate) &&
    (range.endDate === undefined || occurrenceDate <= range.endDate)
  );
}

function getOccurrenceQuoteIds(occurrence: ScheduledRunOccurrence) {
  return occurrence.quotes.flatMap(quote => {
    const quoteId = quote.quoteId?.trim();
    return quoteId ? [quoteId] : [];
  });
}

type OccurrenceRowProps = {
  occurrence: ScheduledRunOccurrence;
  disabled: boolean;
  isSelected: boolean;
  onCopy: () => void;
  onSelected: () => void;
};

function OccurrenceRow({ occurrence, disabled, isSelected, onCopy, onSelected }: OccurrenceRowProps) {
  const { formatDate, formatMessage } = useIntl();
  const radioId = useId();
  const occurrenceDate = getCalendarDate(occurrence.occurrenceDate);
  const date =
    occurrenceDate && isIsoDate(occurrenceDate)
      ? formatDate(occurrenceDate, displayDateFormatOptions)
      : formatMessage({ id: 'scheduledRun.occurrenceReload.unknownDate' });
  const statusMessageId: IntlKey = occurrence.status
    ? (`scheduledRun.occurrenceReload.status.${occurrence.status}` as IntlKey)
    : 'scheduledRun.occurrenceReload.unknown';
  const statusColor = occurrence.status ? OCCURRENCE_STATUS_COLORS[occurrence.status] : 'secondary';
  const hasQuoteIds = getOccurrenceQuoteIds(occurrence).length > 0;
  const copyLabel = formatMessage({ id: 'scheduledRun.occurrenceReload.copyQuoteIds' }, { date });

  return (
    <div className="list-group-item d-flex align-items-center border-bottom">
      <label className="d-flex align-items-center" htmlFor={radioId}>
        <span className="d-flex flex-column">
          <span className="d-flex align-items-center justify-content-between">
            <span className="fw-bold">{date}</span>
          </span>
          <span className="text-secondary">
            <FormattedMessage
              id="scheduledRun.occurrenceReload.quotes"
              values={{ count: occurrence.quotesCount ?? occurrence.quotes.length }}
            />
            {isDefined(occurrence.errorsCount) && occurrence.errorsCount > 0 && (
              <span>
                {' - '}
                <FormattedMessage
                  id="scheduledRun.occurrenceReload.errors"
                  values={{ count: occurrence.errorsCount }}
                />
              </span>
            )}
          </span>
        </span>
      </label>
      {hasQuoteIds && (
        <button
          aria-label={copyLabel}
          className="btn btn-link btn-sm p-0 ms-3"
          onClick={onCopy}
          title={copyLabel}
          type="button"
        >
          <i aria-hidden="true" className="icon icon-outlined icon-xs">
            content_copy
          </i>
        </button>
      )}
      <label className="d-flex flex-grow-1 align-self-stretch align-items-center justify-content-end" htmlFor={radioId}>
        <span className={`badge rounded-pill badge-sm badge-${statusColor}`}>
          <FormattedMessage id={statusMessageId} />
        </span>
      </label>
      <input
        id={radioId}
        aria-label={formatMessage({ id: 'scheduledRun.occurrenceReload.selectOccurrence' }, { date })}
        className="form-check-input ms-3"
        checked={isSelected}
        disabled={disabled}
        name="scheduled-run-occurrence"
        onChange={onSelected}
        type="radio"
      />
    </div>
  );
}
